import { emailConstants } from "_constants";
import { emailService } from "_services";
import { alertActions } from "_actions/alert.actions";

export const emailActions = {
  getemails,
  sendEmailReply,
  updateUnreadEmails,
  updateThreadMessages,
  sendNewThreadEmail,
  getDrafts,
  saveDraft,
  saveDraftOther,
  updateDraft,
  deleteDraft
};

/**
 * Fetches email conversations for a specific lead in a project.
 * Dispatches the appropriate actions based on whether the request succeeds or fails.
 * If `emailNotif` is true, dispatches a notification action.
 * 
 * @param {string} projectId - The ID of the project for which the email history is being fetched.
 * @param {string} leadId - The ID of the lead whose email conversations are being fetched.
 * @param {boolean} emailNotif - A flag indicating whether to dispatch an email notification action.
 * @returns {function} - A function that dispatches actions for request, success, failure, and optional notification.
 */
function getemails(projectId, leadId, emailNotif) {
  return (dispatch) => {
    dispatch(request(projectId, leadId));
    emailService.getLeadEmailConversations(projectId, leadId).then(
      (emails) => {
        // console.log("get email response: ", emails);
        dispatch(success(emails));
      },
      (error) => {
        const { response } = error;
        const { data } = response;
        dispatch(
          failure({
            error_code: "LE001",
            error_message: "Email History not syncing",
          })
        );
        dispatch(
          alertActions.error({
            error_code: "LE001",
            error_message: "Email History not syncing",
          })
        );
      }
    );
    if (emailNotif) {
      dispatch(notification());
    }
  };

  function request(projectId, leadId) {
    return { type: emailConstants.GET_ALL_EMAIL_REQUEST, projectId, leadId };
  }

  function success(emails) {
    return { type: emailConstants.GET_ALL_EMAIL_SUCCESS, emails };
  }

  function failure(error) {
    return { type: emailConstants.GET_ALL_EMAIL_FAILURE, error };
  }

  function notification() {
    return { type: emailConstants.EMAIL_NOTIFICATION_REQUEST };
  }
}

/**
 * Sends a reply email for a specific lead in a project.
 * Dispatches the appropriate actions based on whether the request succeeds or fails.
 * 
 * @param {string} projectId - The ID of the project for which the email reply is being sent.
 * @param {string} leadId - The ID of the lead to whom the email reply is being sent.
 * @param {Object} replyRequest - The request object containing the details of the email reply.
 * @returns {function} - A function that dispatches actions for request, success, and failure.
 */
function sendEmailReply(projectId, leadId, replyRequest) {
  return (dispatch) => {
    dispatch(request(projectId, leadId, replyRequest));
    emailService.agentEmailReply(projectId, leadId, replyRequest).then(
      (emails) => {
        // console.log("response: ", emails);
        dispatch(success(emails));
      },
      (error) => {
        const { response } = error;
        const { data } = response;
        // console.log("email data: ", data);
        dispatch(
          failure({
            error_code: "LC002",
            error_message: "Email Reply unable to send",
          })
        );
        dispatch(
          alertActions.error({
            error_code: "LC002",
            error_message: "Email Reply unable to send",
          })
        );
      }
    );
  };

  function request(projectId, leadId) {
    return {
      type: emailConstants.EMAIL_REPLY_REQUEST,
      projectId,
      leadId,
    };
  }

  function success(emails) {
    return { type: emailConstants.EMAIL_REPLY_SUCCESS, emails };
  }

  function failure(error) {
    return { type: emailConstants.EMAIL_REPLY_FAILURE, error };
  }
}

/**
 * Sends a new email thread for a specific lead in a project.
 * Dispatches the appropriate actions based on whether the request succeeds or fails.
 * 
 * @param {string} projectId - The ID of the project for which the new email thread is being created.
 * @param {string} leadId - The ID of the lead to whom the new email thread is being sent.
 * @param {Object} threadRequest - The request object containing the details of the new email thread.
 * @returns {function} - A function that dispatches actions for request, success, and failure.
 */
function sendNewThreadEmail(projectId, leadId, threadRequest) {
  return (dispatch) => {
    dispatch(request(projectId, leadId, threadRequest));
    emailService.agentNewThreadEmail(projectId, leadId, threadRequest).then(
      (emails) => {
        // console.log("response: ", emails);
        dispatch(success(emails));
      },
      (error) => {
        const { response } = error;
        const { data } = response;
        // console.log("email data: ", data);
        dispatch(
          failure({
            error_code: "LC002",
            error_message: "Email Reply unable to send",
          })
        );
        dispatch(
          alertActions.error({
            error_code: "LC002",
            error_message: "Email Reply unable to send",
          })
        );
      }
    );
  };

  function request(projectId, leadId) {
    return {
      type: emailConstants.EMAIL_REPLY_REQUEST,
      projectId,
      leadId,
    };
  }

  function success(emails) {
    return { type: emailConstants.EMAIL_REPLY_SUCCESS, emails };
  }

  function failure(error) {
    return { type: emailConstants.EMAIL_REPLY_FAILURE, error };
  }
}

/**
 * Updates the unread emails for a specific lead in a project.
 * Dispatches the appropriate actions based on whether the request succeeds or fails.
 * 
 * @param {string} projectId - The ID of the project for which the unread emails are being updated.
 * @param {string} leadId - The ID of the lead whose unread emails are being updated.
 * @param {Object} unreadRequest - The request object containing the details for updating the unread emails.
 * @returns {function} - A function that dispatches actions for request, success, and failure.
 */
function updateUnreadEmails(projectId, leadId, unreadRequest) {
  return (dispatch) => {
    dispatch(request(projectId, leadId));
    emailService.updateUnreadEmails(projectId, leadId, unreadRequest).then(
      (emails) => {
        // console.log('response: ', emails)
        dispatch(success(emails));
      },
      (error) => {
        const { response } = error;
        const { data } = response;
        // console.log("email error ", data);
        dispatch(
          failure({
            error_code: "LC001",
            error_message: "Email History not syncing",
          })
        );
        dispatch(
          alertActions.error({
            error_code: "LC001",
            error_message: "Email History not syncing",
          })
        );
      }
    );
  };

  function request(projectId, leadId) {
    return {
      type: emailConstants.UPDATE_EMAIL_UNREAD_REQUEST,
      projectId,
      leadId,
    };
  }

  function success(emails) {
    return { type: emailConstants.UPDATE_EMAIL_UNREAD_SUCCESS, emails };
  }

  function failure(error) {
    return { type: emailConstants.UPDATE_EMAIL_UNREAD_FAILURE, error };
  }
}

/**
 * Updates the thread messages for a specific lead in a project.
 * Dispatches the appropriate actions based on whether the request succeeds or fails.
 * 
 * @param {string} projectId - The ID of the project for which the thread messages are being updated.
 * @param {string} leadId - The ID of the lead whose thread messages are being updated.
 * @param {Object} thread - The request object containing the thread details to be updated.
 * @returns {function} - A function that dispatches actions for request, success, and failure.
 */
function updateThreadMessages(projectId, leadId, thread) {
  return (dispatch) => {
    dispatch(request(projectId, leadId));
    emailService.updateThreadMessages(projectId, leadId, thread).then(
      (emails) => {
        // console.log('response update thread: ', emails)
        dispatch(success(emails));
      },
      (error) => {
        const { response } = error;
        const { data } = response;
        // console.log("email error ", data);
        dispatch(
          failure({
            error_code: "LC001",
            error_message: "Email History not syncing",
          })
        );
        dispatch(
          alertActions.error({
            error_code: "LC001",
            error_message: "Email History not syncing",
          })
        );
      }
    );
  };

  function request(projectId, leadId) {
    return {
      type: emailConstants.UPDATE_EMAIL_UNREAD_REQUEST,
      projectId,
      leadId,
    };
  }

  function success(emails) {
    return { type: emailConstants.UPDATE_EMAIL_UNREAD_SUCCESS, emails };
  }

  function failure(error) {
    return { type: emailConstants.UPDATE_EMAIL_UNREAD_FAILURE, error };
  }
}

/**
 * Retrieves email drafts for a specific lead in a project.
 * Dispatches the appropriate actions based on whether the request succeeds or fails.
 * 
 * @param {string} projectId - The ID of the project from which to retrieve the email drafts.
 * @param {string} leadId - The ID of the lead for which to retrieve the email drafts.
 * @returns {function} - A function that dispatches actions for request, success, and failure.
 */
function getDrafts(projectId, leadId) {
  return (dispatch) => {
    dispatch(request(projectId, leadId));
    emailService.getEmailDrafts(projectId, leadId).then(
      (drafts) => {
        // console.log('response drafts list: ', drafts)
        dispatch(success(drafts));
      },
      (error) => {
        const { response } = error;
        const { data } = response;
        console.log("draft error ", data);
        dispatch(
          failure({
            error_code: "ED001",
            error_message: "Email Drafts not syncing",
          })
        );
        dispatch(
          alertActions.error({
            error_code: "ED001",
            error_message: "Email Drafts not syncing",
          })
        );
      }
    );
  };

  function request() {
    return {type: emailConstants.EMAIL_DRAFT_REQUEST};
  }

  function success(drafts) {
    return { type: emailConstants.EMAIL_ALL_DRAFT_SUCCESS, drafts };
  }

  function failure(error) {
    return { type: emailConstants.EMAIL_DRAFT_FAILURE, error };
  }
}

/**
 * Saves an email draft for a specific lead in a project.
 * Dispatches the appropriate actions based on whether the request succeeds or fails.
 * 
 * @param {string} projectId - The ID of the project where the draft is being saved.
 * @param {string} leadId - The ID of the lead for whom the draft is being saved.
 * @param {Object} draftRequest - The data for the draft being saved, typically including subject, body, etc.
 * @returns {function} - A function that dispatches actions for request, success, and failure.
 */
function saveDraftOther(projectId, leadId, draftRequest) {
  return (dispatch) => {
    dispatch(request());
    emailService.saveDraft(projectId, leadId, draftRequest).then(
      (drafts) => {
        console.log('response drafts list: ', drafts)
        dispatch(success(drafts));
      },
      (error) => {
        const { response } = error;
        const { data } = response;
        console.log("draft error ", data);
        dispatch(
          failure({
            error_code: "ED001",
            error_message: "Email Drafts not syncing",
          })
        );
        dispatch(
          alertActions.error({
            error_code: "ED001",
            error_message: "Email Drafts not syncing",
          })
        );
      }
    );
  };

  function request(projectId, leadId) {
    return {
      type: emailConstants.EMAIL_REPLY_REQUEST,
      projectId,
      leadId,
    };
  }

  function success(emails) {
    return { type: emailConstants.EMAIL_REPLY_SUCCESS, emails };
  }

  function failure(error) {
    return { type: emailConstants.EMAIL_REPLY_FAILURE, error };
  }
}

/**
 * Saves an email draft for a specific lead in a project.
 * Dispatches the appropriate actions based on whether the request succeeds or fails.
 * 
 * @param {string} projectId - The ID of the project where the draft is being saved.
 * @param {string} leadId - The ID of the lead for whom the draft is being saved.
 * @param {Object} draftRequest - The data for the draft being saved, typically including subject, body, etc.
 * @returns {function} - A function that dispatches actions for request, success, and failure.
 */
function saveDraft(projectId, leadId, draftRequest) {
  return (dispatch) => {
    dispatch(request());
    emailService.saveDraft(projectId, leadId, draftRequest).then(
      (drafts) => {
        console.log('response drafts list: ', drafts)
        dispatch(success(drafts));
      },
      (error) => {
        const { response } = error;
        const { data } = response;
        console.log("draft error ", data);
        dispatch(
          failure({
            error_code: "ED001",
            error_message: "Email Drafts not syncing",
          })
        );
        dispatch(
          alertActions.error({
            error_code: "ED001",
            error_message: "Email Drafts not syncing",
          })
        );
      }
    );
  };

  function request() {
    return {type: emailConstants.EMAIL_DRAFT_REQUEST};
  }

  function success(drafts) {
    return { type: emailConstants.EMAIL_DRAFT_SUCCESS, drafts};
  }

  function failure(error) {
    return { type: emailConstants.EMAIL_DRAFT_FAILURE, error };
  }
}

/**
 * Updates an existing email draft for a specific lead in a project.
 * Dispatches the appropriate actions based on whether the request succeeds or fails.
 * 
 * @param {string} projectId - The ID of the project where the draft is being updated.
 * @param {string} leadId - The ID of the lead for whom the draft is being updated.
 * @param {Object} draftRequest - The data for the draft being updated, typically including subject, body, etc.
 * @returns {function} - A function that dispatches actions for request, success, and failure.
 */
function updateDraft(projectId, leadId, draftRequest) {
  return (dispatch) => {
    dispatch(request());
    emailService.updateDraft(projectId, leadId, draftRequest).then(
      (drafts) => {
        console.log('response drafts list: ', drafts)
        dispatch(success(drafts));
      },
      (error) => {
        const { response } = error;
        const { data } = response;
        console.log("draft error ", data);
        dispatch(
          failure({
            error_code: "ED001",
            error_message: "Email Drafts not syncing",
          })
        );
        dispatch(
          alertActions.error({
            error_code: "ED001",
            error_message: "Email Drafts not syncing",
          })
        );
      }
    );
  };

  function request() {
    return {type: emailConstants.EMAIL_DRAFT_REQUEST};
  }

  function success(drafts) {
    return { type: emailConstants.EMAIL_DRAFT_SUCCESS, drafts };
  }

  function failure(error) {
    return { type: emailConstants.EMAIL_DRAFT_FAILURE, error };
  }
}

/**
 * Deletes an email draft for a specific lead in a project.
 * Dispatches the appropriate actions based on whether the request succeeds or fails.
 * 
 * @param {string} projectId - The ID of the project from which the draft will be deleted.
 * @param {string} leadId - The ID of the lead whose draft is being deleted.
 * @param {string} draftId - The ID of the draft being deleted.
 * @returns {function} - A function that dispatches actions for request, success, and failure.
 */
function deleteDraft(projectId, leadId, draftId) {
  return (dispatch) => {
    dispatch(request());
    emailService.deleteDraft(projectId, leadId, draftId).then(
      (drafts) => {
        console.log('response drafts list: ', drafts)
        dispatch(success(drafts));
      },
      (error) => {
        const { response } = error;
        const { data } = response;
        console.log("draft error ", data);
        dispatch(
          failure({
            error_code: "ED001",
            error_message: "Email Drafts not syncing",
          })
        );
        dispatch(
          alertActions.error({
            error_code: "ED001",
            error_message: "Email Drafts not syncing",
          })
        );
      }
    );
  };

  function request() {
    return {type: emailConstants.EMAIL_DRAFT_REQUEST};
  }

  function success(drafts) {
    return { type: emailConstants.EMAIL_DRAFT_SUCCESS, drafts };
  }

  function failure(error) {
    return { type: emailConstants.EMAIL_DRAFT_FAILURE, error };
  }
}
